// PLAN-cockpit-v2-iter1 Phase 4: Reflect Lens 強化
// SubSidebar に filter (Period / Show / Sort / Domain) を構成
// Maintenance filter は廃止、代わりに Sort (Stale first / Review due) で代替 (2026-05-11)
// Article の sourced_count 識別、Entry score は廃止 (レビュー M3)

import { useEffect, useMemo, useState } from 'react'
import { apiFetch } from '../../api/client'
import { ResultRow } from '../components/common/ResultRow'
import { EmptyState } from '../components/common/EmptyState'
import { useSelectedItem } from '../hooks/useSelectedItem'
import { useV2Router } from '../router'
import { useViewport } from '../hooks/useViewport'
import { FilterBar } from '../components/FilterBar'
import type { SearchHit, SearchResponse } from '../../types'

// Domain Lens へのショートカットが存在するドメイン
const DOMAIN_LENS_MAP: Record<string, 'finance' | 'health' | 'mind'> = {
  finance: 'finance',
  health: 'health',
  mind: 'mind',
}

type Preset = 'week' | 'month' | 'quarter' | 'year'
type ItemFilter = 'all' | 'entries' | 'articles' | 'referenced'
type Sort = 'newest' | 'referenced' | 'stale' | 'review_due' | 'forgotten'

const SORT_OPTIONS: { v: Sort; label: string; hint: string }[] = [
  { v: 'newest', label: 'Newest', hint: '作成日が新しい順' },
  { v: 'referenced', label: 'Most referenced', hint: 'Article 被参照数の多い順' },
  { v: 'stale', label: 'Stale first', hint: '陳腐化期限が近い・過ぎたもの順' },
  { v: 'review_due', label: 'Review due', hint: 'レビュー期限が近い・過ぎたもの順' },
  { v: 'forgotten', label: 'Forgotten', hint: '未参照で古い知識を上位に' },
]

const ACTIVE_DOMAINS = ['finance', 'health', 'mind', 'relation', 'science-tech', 'general'] as const

function rangeOf(p: Preset): { since: string; until: string; label: string } {
  const now = new Date()
  const until = now.toISOString()
  const past = new Date(now)
  if (p === 'week') past.setDate(now.getDate() - 7)
  if (p === 'month') past.setMonth(now.getMonth() - 1)
  if (p === 'quarter') past.setMonth(now.getMonth() - 3)
  if (p === 'year') past.setFullYear(now.getFullYear() - 1)
  return {
    since: past.toISOString(),
    until,
    label: p === 'week' ? 'Past 7 days' : p === 'month' ? 'Past 30 days' : p === 'quarter' ? 'Past 90 days' : 'Past year',
  }
}

function rangeHeading(p: Preset): string {
  return p === 'week' ? 'Last week' : p === 'month' ? 'Last month' : p === 'quarter' ? 'Last quarter' : 'Last year'
}

interface DomainCount { domain: string; count: number }

// Sort 関数: クライアント側で 200 件サンプルを並び替える
function applySort(list: SearchHit[], sort: Sort): SearchHit[] {
  if (sort === 'newest') return list // server sort=-created_at のまま
  const copy = list.slice()
  if (sort === 'referenced') {
    // sourced_count DESC (null/undefined は末尾)
    copy.sort((a, b) => {
      const av = a.sourced_count ?? -1
      const bv = b.sourced_count ?? -1
      if (av === bv) return a.created_at < b.created_at ? 1 : -1
      return bv - av
    })
  } else if (sort === 'stale') {
    // stale_by ASC (近い・過ぎた順)、null は末尾
    copy.sort((a, b) => {
      const av = a.stale_by ?? null
      const bv = b.stale_by ?? null
      if (av === bv) return a.created_at < b.created_at ? 1 : -1
      if (av === null) return 1
      if (bv === null) return -1
      return av < bv ? -1 : 1
    })
  } else if (sort === 'review_due') {
    copy.sort((a, b) => {
      const av = a.review_by ?? null
      const bv = b.review_by ?? null
      if (av === bv) return a.created_at < b.created_at ? 1 : -1
      if (av === null) return 1
      if (bv === null) return -1
      return av < bv ? -1 : 1
    })
  } else if (sort === 'forgotten') {
    // 未参照 (sourced_count = 0 or null) かつ古い順を上位、参照あり Article / Entry は末尾
    // Entry は sourced_count が常に undefined → "未参照" 扱いになるが、Article との混在を避けるため
    // Article(synthesized) のみを対象として、それ以外は末尾に置く
    copy.sort((a, b) => {
      const aIsForgottenArticle = a.origin === 'synthesized' && (a.sourced_count ?? 0) === 0
      const bIsForgottenArticle = b.origin === 'synthesized' && (b.sourced_count ?? 0) === 0
      if (aIsForgottenArticle !== bIsForgottenArticle) return aIsForgottenArticle ? -1 : 1
      // 同グループ内では古い順 (created_at ASC)
      return a.created_at < b.created_at ? -1 : 1
    })
  }
  return copy
}

// ===== ReflectFilterContent: デスクトップ Sidebar とモバイル FilterBar 両方で使う =====

interface ReflectFilterContentProps {
  preset: Preset
  itemFilter: ItemFilter
  sort: Sort
  domain: string
  onChange: (p: Preset, f: ItemFilter, s: Sort, d: string) => void
}

// Pill style helper — Explore Lens と統一
const pillBase = 'text-xs px-2.5 py-1 rounded-full border transition'
const pillActive = 'bg-accent-primary text-bg-surface border-accent-primary'
const pillIdle = 'border-border-subtle text-text-muted hover:border-border-strong'

export function ReflectFilterContent({ preset, itemFilter, sort, domain, onChange }: ReflectFilterContentProps) {
  const { navigate } = useV2Router()

  return (
    <div className="space-y-4 text-sm">
      {/* Period */}
      <div>
        <h3 className="text-xs uppercase tracking-wide text-text-subtle mb-1.5">Period</h3>
        <div className="flex flex-wrap gap-1.5">
          {(['week', 'month', 'quarter', 'year'] as Preset[]).map(p => (
            <button key={p} type="button"
              onClick={() => onChange(p, itemFilter, sort, domain)}
              aria-pressed={preset === p}
              className={`${pillBase} ${preset === p ? pillActive : pillIdle}`}>
              {p === 'week' ? '7 days' : p === 'month' ? '30 days' : p === 'quarter' ? '90 days' : '1 year'}
            </button>
          ))}
        </div>
      </div>

      {/* Show */}
      <div>
        <h3 className="text-xs uppercase tracking-wide text-text-subtle mb-1.5">Show</h3>
        <div className="flex flex-wrap gap-1.5">
          {([
            { v: 'all', label: 'All' },
            { v: 'entries', label: 'Entries' },
            { v: 'articles', label: 'Articles' },
            { v: 'referenced', label: 'Referenced' },
          ] as { v: ItemFilter; label: string }[]).map(({ v, label }) => (
            <button key={v} type="button"
              onClick={() => onChange(preset, v, sort, domain)}
              aria-pressed={itemFilter === v}
              className={`${pillBase} ${itemFilter === v ? pillActive : pillIdle}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Sort */}
      <div>
        <h3 className="text-xs uppercase tracking-wide text-text-subtle mb-1.5">Sort</h3>
        <div className="flex flex-wrap gap-1.5">
          {SORT_OPTIONS.map(({ v, label, hint }) => (
            <button key={v} type="button"
              onClick={() => onChange(preset, itemFilter, v, domain)}
              aria-pressed={sort === v}
              title={hint}
              className={`${pillBase} ${sort === v ? pillActive : pillIdle}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Domain */}
      <div>
        <h3 className="text-xs uppercase tracking-wide text-text-subtle mb-1.5">Domain</h3>
        <div className="flex flex-wrap gap-1.5 items-center">
          <button type="button" onClick={() => onChange(preset, itemFilter, sort, '')}
            aria-pressed={!domain}
            className={`${pillBase} ${!domain ? pillActive : pillIdle}`}>
            All
          </button>
          {ACTIVE_DOMAINS.map(d => (
            <button key={d} type="button"
              onClick={() => onChange(preset, itemFilter, sort, d)}
              aria-pressed={domain === d}
              className={`${pillBase} capitalize ${domain === d ? pillActive : pillIdle}`}>
              {d}
            </button>
          ))}
          {domain && DOMAIN_LENS_MAP[domain] && (
            <button type="button"
              onClick={() => navigate(DOMAIN_LENS_MAP[domain]!)}
              title={`Open ${domain} Lens`}
              className="text-[10px] text-accent-primary hover:underline whitespace-nowrap px-1">
              Open ↗
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ===== ReflectSidebar: デスクトップ用 (SessionStorage 橋渡し) =====

export function ReflectSidebar() {
  const [preset, setPreset] = useState<Preset>(() => (window.sessionStorage.getItem('reflect_preset') as Preset) ?? 'month')
  const [itemFilter, setItemFilter] = useState<ItemFilter>(() => (window.sessionStorage.getItem('reflect_item_filter') as ItemFilter) ?? 'all')
  const [sort, setSort] = useState<Sort>(() => (window.sessionStorage.getItem('reflect_sort') as Sort) ?? 'newest')
  const [domain, setDomain] = useState<string>(() => window.sessionStorage.getItem('reflect_domain') ?? '')

  const update = (p: Preset, f: ItemFilter, s: Sort, d: string) => {
    setPreset(p); setItemFilter(f); setSort(s); setDomain(d)
    window.sessionStorage.setItem('reflect_preset', p)
    window.sessionStorage.setItem('reflect_item_filter', f)
    window.sessionStorage.setItem('reflect_sort', s)
    window.sessionStorage.setItem('reflect_domain', d)
    window.dispatchEvent(new CustomEvent('reflect_filter_change'))
  }

  return (
    <div className="p-4">
      <h2 className="text-xs uppercase tracking-wide text-text-subtle mb-3">Filters</h2>
      <ReflectFilterContent preset={preset} itemFilter={itemFilter} sort={sort} domain={domain} onChange={update} />
    </div>
  )
}

export function ReflectLens() {
  const viewport = useViewport()
  const [preset, setPreset] = useState<Preset>(() => (window.sessionStorage.getItem('reflect_preset') as Preset) ?? 'month')
  const [itemFilter, setItemFilter] = useState<ItemFilter>(() => (window.sessionStorage.getItem('reflect_item_filter') as ItemFilter) ?? 'all')
  const [sort, setSort] = useState<Sort>(() => (window.sessionStorage.getItem('reflect_sort') as Sort) ?? 'newest')
  const [domain, setDomain] = useState<string>(() => window.sessionStorage.getItem('reflect_domain') ?? '')
  const { setSelectedItem, selectedItem } = useSelectedItem()

  // モバイル FilterBar からのフィルター変更 (sessionStorage + event で ReflectLens 自身のstateも更新)
  const handleMobileFilterChange = (p: Preset, f: ItemFilter, s: Sort, d: string) => {
    setPreset(p); setItemFilter(f); setSort(s); setDomain(d)
    window.sessionStorage.setItem('reflect_preset', p)
    window.sessionStorage.setItem('reflect_item_filter', f)
    window.sessionStorage.setItem('reflect_sort', s)
    window.sessionStorage.setItem('reflect_domain', d)
    window.dispatchEvent(new CustomEvent('reflect_filter_change'))
  }
  const [timeline, setTimeline] = useState<SearchHit[]>([])
  const [tlLoading, setTlLoading] = useState(false)
  const [counts, setCounts] = useState<DomainCount[]>([])
  const [kpiTotal, setKpiTotal] = useState<number | null>(null)

  // SubSidebar からの filter 変更を受け取る
  useEffect(() => {
    const handler = () => {
      setPreset((window.sessionStorage.getItem('reflect_preset') as Preset) ?? 'month')
      setItemFilter((window.sessionStorage.getItem('reflect_item_filter') as ItemFilter) ?? 'all')
      setSort((window.sessionStorage.getItem('reflect_sort') as Sort) ?? 'newest')
      setDomain(window.sessionStorage.getItem('reflect_domain') ?? '')
    }
    window.addEventListener('reflect_filter_change', handler)
    return () => window.removeEventListener('reflect_filter_change', handler)
  }, [])

  const range = useMemo(() => rangeOf(preset), [preset])

  // Timeline + KPI
  useEffect(() => {
    const ac = new AbortController()
    setTlLoading(true)
    const q: Record<string, string | number | undefined> = {
      since: range.since, until: range.until, sort: '-created_at', limit: 200,
    }
    if (domain) q.domain = domain
    if (domain) q.domain_mode = 'broad'
    apiFetch<SearchResponse>('/api/search', { query: q, signal: ac.signal })
      .then(res => {
        setTimeline(res.entries ?? [])
        setKpiTotal(res.total ?? 0)
      })
      .catch(() => { setTimeline([]); setKpiTotal(null) })
      .finally(() => setTlLoading(false))
    return () => ac.abort()
  }, [range.since, range.until, domain])

  // Domain pulse
  useEffect(() => {
    const ac = new AbortController()
    Promise.all(
      ACTIVE_DOMAINS.map(d =>
        apiFetch<SearchResponse>('/api/search', { query: { domain: d, domain_mode: 'broad', since: range.since, until: range.until, limit: 1 }, signal: ac.signal })
          .then(r => ({ domain: d as string, count: r.total ?? 0 }))
          .catch(() => ({ domain: d as string, count: 0 }))
      )
    ).then(setCounts)
    return () => ac.abort()
  }, [range.since, range.until])

  const maxCount = Math.max(1, ...counts.map(c => c.count))

  // Timeline のフィルタリング (Article sourced_count score 識別、Entry score は廃止 M3)
  const filteredTimeline = useMemo(() => {
    let list = timeline
    if (itemFilter === 'entries') list = list.filter(h => h.origin !== 'synthesized')
    else if (itemFilter === 'articles') list = list.filter(h => h.origin === 'synthesized')
    else if (itemFilter === 'referenced') list = list.filter(h => h.origin === 'synthesized' && (h.sourced_count ?? 0) > 0)
    return applySort(list, sort)
  }, [timeline, itemFilter, sort])

  // top 10% の sourced_count threshold
  const scoreThreshold = useMemo(() => {
    const scores = timeline.filter(h => h.origin === 'synthesized' && h.sourced_count != null).map(h => h.sourced_count!)
    if (scores.length === 0) return Infinity
    scores.sort((a, b) => b - a)
    return scores[Math.max(0, Math.floor(scores.length * 0.1))] ?? Infinity
  }, [timeline])

  return (
    <div className="h-full flex flex-col">
      {/* モバイル: FilterBar (デスクトップは ReflectSidebar を使う) */}
      {viewport === 'mobile' && (
        <FilterBar label="期間・フィルター">
          <ReflectFilterContent
            preset={preset}
            itemFilter={itemFilter}
            sort={sort}
            domain={domain}
            onChange={handleMobileFilterChange}
          />
        </FilterBar>
      )}
      <div className="flex-1 overflow-y-auto">
      <div className="max-w-3xl mx-auto w-full px-8 py-10 space-y-10">
        <header className="space-y-3">
          <p className="text-xs uppercase tracking-wide text-text-subtle">Reflect · {range.label}</p>
          <h1 className="text-2xl font-medium text-text-base">{rangeHeading(preset)}</h1>
        </header>

        {/* Period summary */}
        <section>
          <h2 className="text-xs uppercase tracking-wide text-text-subtle mb-3">Period summary</h2>
          <p className="text-base text-text-base">
            <span className="font-medium">{kpiTotal ?? '—'}</span> entries / articles{domain ? ` (${domain})` : ''} created
          </p>
        </section>

        {/* Domain pulse */}
        <section>
          <h2 className="text-xs uppercase tracking-wide text-text-subtle mb-3">Domain pulse</h2>
          <ul className="space-y-2 text-sm">
            {counts.map(c => (
              <li key={c.domain} className="flex items-center gap-3">
                <span className="w-20 text-text-base capitalize">{c.domain}</span>
                <span className="flex-1 h-2 bg-border-subtle rounded overflow-hidden">
                  <span className={`block h-full bg-domain-${c.domain}`} style={{ width: `${(c.count / maxCount) * 100}%` }} aria-hidden />
                </span>
                <span className="w-12 text-right text-text-muted font-mono text-xs">{c.count}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Timeline */}
        <section>
          <h2 className="text-xs uppercase tracking-wide text-text-subtle mb-1">Timeline</h2>
          <p className="text-xs text-text-subtle mb-3">
            Sort: {SORT_OPTIONS.find(o => o.v === sort)?.label ?? sort}
            {itemFilter !== 'all' && <> · Filter: {itemFilter}</>}
          </p>
          {tlLoading ? (
            <p className="text-sm text-text-subtle">Loading…</p>
          ) : filteredTimeline.length === 0 ? (
            <EmptyState title="No items in this period" description="期間を伸ばすか別の期間を選択してください" />
          ) : (
            <ul className="border-t border-border-subtle">
              {filteredTimeline.map(hit => {
                const isArticle = hit.origin === 'synthesized'
                const itemType: 'entry' | 'article' = isArticle ? 'article' : 'entry'
                const isHighlighted = isArticle && (hit.sourced_count ?? 0) >= scoreThreshold && scoreThreshold !== Infinity
                return (
                  <li key={hit.id} className={isHighlighted ? 'relative' : ''}>
                    {isHighlighted && (
                      <span className="absolute left-0 top-0 bottom-0 w-0.5 bg-accent-primary" aria-hidden />
                    )}
                    <ResultRow
                      id={hit.id}
                      title={hit.title}
                      one_line={hit.one_line}
                      origin={hit.origin}
                      subjectivity={hit.subjectivity}
                      source_project={hit.source_project ?? null}
                      domains={hit.domains ?? []}
                      visibility={hit.visibility ?? null}
                      has_decision={hit.has_decision}
                      x_status={hit.x_status ?? null}
                      created_at={hit.created_at}
                      selected={selectedItem?.id === hit.id}
                      onClick={() => setSelectedItem({ id: hit.id, itemType })}
                    />
                    {/* Article の sourced_count 表示 (Entry の score は廃止 M3) */}
                    {isArticle && hit.sourced_count != null && hit.sourced_count > 0 && (
                      <div className="px-4 pb-1 text-[11px] text-accent-primary">
                        ×{hit.sourced_count} references
                      </div>
                    )}
                    {/* Sort=stale / review_due のとき、対応する期限を表示 */}
                    {sort === 'stale' && hit.stale_by && (
                      <div className="px-4 pb-1 text-[11px] text-text-subtle">
                        stale_by: {hit.stale_by.slice(0, 10)}
                      </div>
                    )}
                    {sort === 'review_due' && hit.review_by && (
                      <div className="px-4 pb-1 text-[11px] text-text-subtle">
                        review_by: {hit.review_by.slice(0, 10)}
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      </div>
      </div>
    </div>
  )
}
